// Generic currency detection & formatting for dynamic Aras fields.
//
// Detection strategy (priority order):
//   1. Label suffix in parentheses matching a known ISO-4217-like code or
//      common symbol — e.g. "Học phí (VND)", "Salary (USD)", "Giá (đ)".
//   2. Field name pattern — snake_case or camelCase ending with a money-ish
//      suffix (fee, amount, price, value, salary, revenue, cost, ...).
//
// Why: Aras has no dedicated "money" data_type; decimal/float is reused for
// both currency and plain numerics. Letting the label drive detection means
// business users can opt a field into money formatting from Aras without
// touching code.

const CURRENCY_LABEL_MAP: Record<string, string> = {
  VND: "đ",
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  CNY: "¥",
  KRW: "₩",
  SGD: "S$",
  THB: "฿",
  AUD: "A$",
  CAD: "C$",
  HKD: "HK$",
  TWD: "NT$",
  Đ: "đ",
  đ: "đ",
  "₫": "₫",
};

// Money-ish field-name suffixes (matched on snake_case form). Kept tight on
// purpose — generic words like "total"/"value"/"balance" are too ambiguous
// (they appear in non-money fields too), so we rely on the label suffix path
// to opt those in.
const MONEY_NAME_SUFFIXES = [
  "fee",
  "amount",
  "price",
  "salary",
  "revenue",
  "cost",
  "income",
  "expense",
  "budget",
  "wage",
  "hoc_phi",
  "luong",
  "doanh_thu",
  "chi_phi",
];

const HARDCODED_LEGACY_NAMES = new Set([
  "estimatedValue",
  "amount",
  "totalRevenue",
  "annualRevenue",
]);

/** Convert a camelCase or PascalCase identifier to snake_case. */
const toSnakeCase = (name: string): string =>
  name
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1_$2")
    .toLowerCase();

/**
 * Extract a currency code/symbol from a label by inspecting the last
 * parenthesised group. Returns null when nothing matches.
 *
 * Examples:
 *   "Học phí (VND)"      -> "đ"
 *   "Salary (USD)"       -> "$"
 *   "Giá trị HĐ (đ)"     -> "đ"
 *   "Quantity (units)"   -> null
 */
export const extractCurrencySymbolFromLabel = (
  label?: string,
): string | null => {
  if (!label) return null;
  const match = label.match(/\(([^()]+)\)\s*$/);
  if (!match) return null;
  const token = match[1].trim();
  if (CURRENCY_LABEL_MAP[token]) return CURRENCY_LABEL_MAP[token];
  // Allow case-insensitive 3-letter codes
  const upper = token.toUpperCase();
  if (CURRENCY_LABEL_MAP[upper]) return CURRENCY_LABEL_MAP[upper];
  return null;
};

/**
 * Decide whether a numeric field should be rendered as money, and which
 * symbol to use. Returns null when the field is not a currency field.
 */
export const detectCurrencySymbol = ({
  label,
  mappedName,
}: {
  label?: string;
  mappedName?: string;
}): string | null => {
  // 1. Label-based detection takes priority
  const labelSymbol = extractCurrencySymbolFromLabel(label);
  if (labelSymbol) return labelSymbol;

  if (!mappedName) return null;

  // 2. Legacy hardcoded names — keep behaviour consistent with what shipped
  if (HARDCODED_LEGACY_NAMES.has(mappedName)) return "đ";

  // 3. Name-pattern detection
  const snake = toSnakeCase(mappedName);
  const matchesSuffix = MONEY_NAME_SUFFIXES.some(
    (suffix) => snake === suffix || snake.endsWith(`_${suffix}`),
  );
  if (matchesSuffix) return "đ";

  return null;
};

/** Convenience predicate. */
export const isCurrencyField = (args: {
  label?: string;
  mappedName?: string;
}): boolean => detectCurrencySymbol(args) !== null;

/**
 * Format a numeric value as currency for read-only display.
 * VND is rendered without fractional digits; other currencies preserve up to
 * 2 fractional digits (only shown when the underlying number has them).
 */
export const formatCurrencyValue = (
  value: number | string,
  symbol: string,
): string => {
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return String(value);
  const isVnd = symbol === "đ" || symbol === "₫";
  const formatted = isVnd
    ? num.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return `${symbol} ${formatted}`;
};

/**
 * antd InputNumber `formatter` — adds thousand separators and currency
 * prefix while typing.
 */
export const buildCurrencyInputFormatter =
  (symbol: string) =>
  (value: string | number | undefined): string => {
    if (value === undefined || value === null || value === "") return "";
    return `${symbol} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

/**
 * antd InputNumber `parser` — strips currency prefix and thousand separators
 * so the underlying form value is a clean number string. Returns "" for
 * empty input so antd treats the field as cleared (not 0).
 */
export const buildCurrencyInputParser =
  (symbol: string) =>
  (value: string | undefined): number => {
    if (!value) return "" as unknown as number;
    // $& = the entire matched substring (escapes regex metachars correctly)
    const escaped = symbol.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`${escaped}\\s?|,`, "g");
    return value.replace(re, "") as unknown as number;
  };
